import { createEffect, createSignal, For, onCleanup, onMount } from "solid-js";
import { useParams } from "@solidjs/router";
import { getSession } from "../api/sessions";
import { LanguageBadge } from "../components/LanguageBadge";
import { PanelHeader } from "../components/PanelHeader";
import type {
  BroadcastEvent,
  UtteranceEvent,
  AnyEvent,
} from "../utils/eventBus";
import { openSessionChannel } from "../utils/eventBus";
import { useTTS } from "../hooks/useTTS";
let leftBox!: HTMLDivElement;
let rightBox!: HTMLDivElement;
const [leftPinned, setLeftPinned] = createSignal(false);
const [rightPinned, setRightPinned] = createSignal(false);
const onScroll = (box: HTMLDivElement, setPinned: (v: boolean) => void) => {
  const atBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 4;
  setPinned(!atBottom);
};

type Role = "caseworker" | "respondent";

interface Line {
  speaker: number; // 1 or 2
  text: string;
  translation?: string;
  translationError?: boolean;
  confidence?: number; // 0..1
  ts?: number;
  lang?: string;
}

export default function PopoutTranscript() {
  const params = useParams();
  const sessionId = params.id as string;
  const role = (params.role as Role) || "caseworker";

  const [status, setStatus] = createSignal<"waiting" | "listening" | "ended">(
    "waiting"
  );

  // Languages for badges
  const [langS1, setLangS1] = createSignal("en-US");
  const [langS2, setLangS2] = createSignal("");

  // Font scale persisted per window
  const storageKey = `popout-fontscale-${sessionId}-${role}`;
  const computeInitialScale = () => {
    const raw = localStorage.getItem(storageKey);
    const n = raw ? Number(raw) : 1;
    return Number.isFinite(n) && n >= 0.85 && n <= 1.4 ? n : 1;
  };
  const [fontScale, setFontScale] = createSignal<number>(computeInitialScale());
  const applyScale = (n: number) => {
    const clamped = Math.min(1.4, Math.max(0.85, n));
    setFontScale(clamped);
    localStorage.setItem(storageKey, String(clamped));
  };

  // Two panel buffers (UI only; wiring later)
  const [leftLines, setLeftLines] = createSignal<Line[]>([]);
  const [rightLines, setRightLines] = createSignal<Line[]>([]);

  const tts = useTTS({
    provider: "webspeech",
    storageKey: `tts-popout-${sessionId}-${role}`,
  });

  // Panel-level auto-scroll
  createEffect(() => {
    leftLines().length;
    queueMicrotask(() => {
      if (!leftPinned() && leftBox) leftBox.scrollTop = leftBox.scrollHeight;
    });
  });
  createEffect(() => {
    rightLines().length;
    queueMicrotask(() => {
      if (!rightPinned() && rightBox)
        rightBox.scrollTop = rightBox.scrollHeight;
    });
  });

  // Keyboard font size shortcuts
  const onKey = (e: KeyboardEvent) => {
    const isPlus = (e.key === "+" || e.key === "=") && (e.ctrlKey || e.metaKey);
    const isMinus = e.key === "-" && (e.ctrlKey || e.metaKey);
    if (!isPlus && !isMinus) return;
    e.preventDefault();
    applyScale(fontScale() + (isPlus ? 0.05 : -0.05));
  };

  // Load session for language badges
  createEffect(() => {
    if (!sessionId) return;
    getSession(sessionId)
      .then((s) => {
        const meta: any = s.metadata || {};
        const s1 = meta.speaker1Lang || s.sourceLanguage || "en-US";
        const s2 = meta.speaker2Lang || s.targetLanguage || "";
        setLangS1(s1);
        setLangS2(s2);
      })
      .catch(() => {});
  });

  // Listen live status + utterances
  let channel: BroadcastChannel | null = null;
  createEffect(() => {
    if (!sessionId) return;
    channel = openSessionChannel(sessionId);
    channel.onmessage = (ev) => {
      const msg = (ev.data || {}) as AnyEvent;
      if (msg.type === "status") {
        if (msg.status === "listening") setStatus("listening");
        if (msg.status === "ended") setStatus("ended");
        return;
      }
      // Ignore config events for badges; languages are fixed per session setup
      if (msg.type === "config") {
        return;
      }
      if (msg.type === "utterance") {
        routeUtterance(msg);
      }
    };
  });

  onMount(() => window.addEventListener("keydown", onKey));
  onCleanup(() => {
    try {
      channel?.close();
    } catch {}
    window.removeEventListener("keydown", onKey);
  });

  const title = () =>
    role === "respondent" ? "Respondent Transcript" : "Caseworker Transcript";

  const langName = (tag: string) => {
    try {
      if (!tag) return "Unknown";
      const dn = new Intl.DisplayNames("en", { type: "language" });
      return dn.of(tag) || tag;
    } catch {
      return tag || "Unknown";
    }
  };

  const LowConfDot = (p: { c?: number }) => (
    <span
      class="inline-block w-1.5 h-1.5 rounded-full align-middle ml-1"
      classList={{
        "bg-red-400": (p.c ?? 1) < 0.6,
        "bg-transparent": (p.c ?? 1) >= 0.6,
      }}
      title={(p.c ?? 1) < 0.6 ? "low confidence" : undefined}
    />
  );

  // Route incoming utterance updates into left/right buffers by role
  function routeUtterance(evt: UtteranceEvent) {
    const isCaseworker = role === "caseworker";

    // --- language-based fallback ---
    const base = (t: string) => (t || "").split("-")[0].toLowerCase();
    const evLang = base((evt.lang as string) || "");
    const s1 = base(langS1());
    const s2 = base(langS2());

    let sp = evt.speaker as 1 | 2;
    if (evLang) {
      // If the SDK says "speaker 1" but the utterance language matches speaker 2's language, treat as speaker 2 (and vice versa).
      if (s2 && evLang === s2 && sp === 1) sp = 2;
      else if (s1 && evLang === s1 && sp === 2) sp = 1;
    }

    if (sp === 1) {
      if (isCaseworker)
        setLeftLines((prev) => upsert(prev, evt, false)); // S1 originals for CW
      else setLeftLines((prev) => upsert(prev, evt, true)); // S1→S2 translations for Respondent
    } else {
      if (isCaseworker) setRightLines((prev) => upsert(prev, evt, true));
      // S2→S1 translations for CW
      else setRightLines((prev) => upsert(prev, evt, false)); // S2 originals for Respondent
    }
  }

  // Upsert by id, merging original/translation fields as they arrive
  function upsert(list: any[], evt: UtteranceEvent, wantTranslation: boolean) {
    const idx = list.findIndex((l) => l.id === evt.id);
    const base =
      idx >= 0 ? { ...list[idx] } : { id: evt.id, speaker: evt.speaker };
    if (!wantTranslation) {
      if (evt.text !== undefined) base.text = evt.text;
      if (evt.confidence !== undefined) base.confidence = evt.confidence;
    } else {
      if (evt.translation !== undefined) base.translation = evt.translation;
      if (evt.translationError) base.translationError = true;
    }
    if (evt.lang !== undefined) base.lang = evt.lang;
    const next = [...list];
    if (idx >= 0) next[idx] = base;
    else next.push(base);
    return next;
  }

  const Bubble = (u: Line) => {
    const side = u.speaker % 2 ? "mr-auto" : "ml-auto";
    const colours = [
      "bg-indigo-100 border border-indigo-500 text-indigo-900",
      "bg-blue-50   border border-blue-400   text-blue-900",
      "bg-slate-50  border border-slate-400  text-slate-900",
      "bg-teal-50   border border-teal-400  text-teal-900",
      "bg-amber-100 border border-amber-500 text-amber-900",
    ];
    const clr = colours[u.speaker % colours.length];
    const label = u.speaker === 1 ? "Speaker 1" : "Speaker 2";
    return (
      <p
        class={`max-w-[80%] ${clr} rounded-xl px-3 py-1 ${side}`}
        style={{ "font-size": `${fontScale()}rem` }}
      >
        <span class="text-xs opacity-70 mr-1">
          {label}
          <LowConfDot c={u.confidence} />
        </span>
        <span>{u.text}</span>
        <For each={u.translation ? [u.translation] : []}>
          {(t) => (
            <span class="block italic text-slate-600 text-xs mt-0.5">{t}</span>
          )}
        </For>
        <For each={u.translationError ? [true] : []}>
          {() => (
            <span class="ml-2 text-[10px] px-1 py-[1px] rounded bg-rose-50 text-rose-700 border border-rose-200">
              translation unavailable
            </span>
          )}
        </For>
      </p>
    );
  };

  const Panel = (p: {
    title: string;
    badge: string;
    lines: Line[];
    showCopyAll?: boolean;
  }) => (
    <div class="card">
      <div class="flex items-center gap-2 mb-2">
        <PanelHeader title={p.title} />
        <LanguageBadge label={p.badge} />
        <For each={p.showCopyAll ? [true] : []}>
          {() => (
            <button
              onClick={() => {
                const text = p.lines
                  .map(
                    (u) =>
                      `[S${u.speaker}] ${u.text}${
                        u.translation ? `  ⇢  ${u.translation}` : ""
                      }`
                  )
                  .join("\n");
                navigator.clipboard.writeText(text);
              }}
              title="Copy all"
              class="text-gray-500 hover:text-gray-700 ml-auto"
            >
              📋
            </button>
          )}
        </For>
      </div>
      <div
        ref={
          p.badge.includes("translation")
            ? (el) => (rightBox = el)
            : (el) => (leftBox = el)
        }
        onScroll={
          p.badge.includes("translation")
            ? () => onScroll(rightBox, setRightPinned)
            : () => onScroll(leftBox, setLeftPinned)
        }
        class="space-y-3 max-h-[70vh] overflow-auto"
      >
        <For each={p.lines}>
          {(u) => (
            <div>
              <Bubble {...u} />
              <For
                each={role === "respondent" && u.speaker === 1 ? [true] : []}
              >
                {() => (
                  <button
                    class="text-xs text-gray-600 hover:text-gray-800 ml-2"
                    title="Play this message"
                    aria-label="Play this message"
                    onClick={() => {
                      const txt = u.translation || u.text || "";
                      if (!txt.trim()) return;
                      if (!tts.enabled()) tts.enable();
                      tts.stop();
                      const targetLang = u.translation ? langS2() : langS1();
                      tts.enqueue({
                        key: `${u.speaker}:${u.ts || u.text}`,
                        text: txt,
                        lang: targetLang,
                      });
                    }}
                  >
                    🔊
                  </button>
                )}
              </For>
            </div>
          )}
        </For>
        <For each={p.lines.length ? [] : [true]}>
          {() => (
            <p class="text-gray-500 text-sm">
              {status() === "waiting"
                ? "Waiting for live transcript…"
                : status() === "ended"
                ? "Session ended"
                : ""}
            </p>
          )}
        </For>
      </div>
    </div>
  );

  return (
    <div class="min-h-[100dvh] overflow-y-auto bg-white">
      <header class="px-4 py-3 border-b flex items-center justify-between">
        <h1 class="text-lg font-semibold">{title()}</h1>
        <span
          class={`text-xs px-2 py-[2px] rounded border ${
            status() === "listening"
              ? "bg-green-50 text-green-700 border-green-200"
              : status() === "ended"
              ? "bg-gray-50 text-gray-600 border-gray-200"
              : "bg-yellow-50 text-yellow-700 border-yellow-200"
          }`}
        >
          {status() === "waiting"
            ? "Waiting for live transcript…"
            : status() === "listening"
            ? "Live"
            : "Session ended"}
        </span>
      </header>
      <main class="p-4">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left/right mapping per role */}
          <Panel
            title="Transcript"
            badge={
              role === "caseworker"
                ? `${langName(langS1())}`
                : `${langName(langS2())} · translation`
            }
            lines={leftLines()}
            showCopyAll={role === "caseworker"}
          />
          <Panel
            title="Transcript"
            badge={
              role === "caseworker"
                ? `${langName(langS1())} · translation`
                : `${langName(langS2())}`
            }
            lines={rightLines()}
          />
        </div>
      </main>
    </div>
  );
}
